import { scoreboardNameFromPlayer } from "./playerDisplayName";

export const LANDING_DISPLAY_STATS = [
  "homeruns",
  "hits",
  "battingAverage",
  "runBattedIn",
];

export const RECOMMENDED_FILTERS = [
  {
    id: "homeruns",
    stat: "homeruns",
    operator: ">=",
    careerValue: 300,
    seasonValue: 30,
    label: "Home Runs",
    abbrev: "HR",
  },
  {
    id: "hits",
    stat: "hits",
    operator: ">=",
    careerValue: 2000,
    seasonValue: 150,
    label: "Hits",
    abbrev: "H",
  },
  {
    id: "runBattedIn",
    stat: "runBattedIn",
    operator: ">=",
    careerValue: 1000,
    seasonValue: 100,
    label: "RBI",
    abbrev: "RBI",
  },
  {
    id: "battingAverage",
    stat: "battingAverage",
    operator: ">=",
    careerValue: 0.3,
    seasonValue: 0.3,
    label: "Batting Average",
    abbrev: "AVG",
  },
];

const landingHrFilter = RECOMMENDED_FILTERS.find((filter) => filter.id === "homeruns");

export const DEFAULT_QUERY_PARAMS = {
  queryType: "career",
  startYear: 1876,
  endYear: 2025,
  selectedStats: [...LANDING_DISPLAY_STATS],
  statisticalFilters: {
    [landingHrFilter.stat]: {
      operator: landingHrFilter.operator,
      value: landingHrFilter.careerValue,
    },
  },
  biographicalFilters: {},
  statusFilters: {},
  teams: [],
  sortBy: "homeruns",
  sortDirection: -1,
  limit: 50,
};

export const QUERY_NAME_SORT_FIELD = "bioData.birthName";
export const GRID_PLAYER_NAME_FIELD = "playerName";
export const QUERY_PAGE_SIZE_OPTIONS = [25, 50, 100];
export const QUERY_TABLE_HEIGHT = 580;

export const QUERY_TYPES = [
  {
    value: "career",
    label: "Career",
    description: "Aggregate stats across a player's full career",
  },
  {
    value: "season",
    label: "Season",
    description: "Single-season lines within a year range",
  },
  {
    value: "team",
    label: "Team rollups",
    description: "Players grouped by franchise — not a team roster filter",
  },
];

export const BIO_FILTER_FIELDS = [
  { value: "nameStartsWith", label: "Name starts with" },
  { value: "nameEndsWith", label: "Name ends with" },
  { value: "birthPlace", label: "Birth place" },
  { value: "bats", label: "Bats" },
  { value: "throws", label: "Throws" },
];

export const QUERY_BUILDER_STAT_META = {
  games: { label: "Games", abbrev: "G", rate: false },
  atBat: { label: "At Bats", abbrev: "AB", rate: false },
  runs: { label: "Runs", abbrev: "R", rate: false },
  hits: { label: "Hits", abbrev: "H", rate: false },
  doubles: { label: "Doubles", abbrev: "2B", rate: false },
  triples: { label: "Triples", abbrev: "3B", rate: false },
  homeruns: { label: "Home Runs", abbrev: "HR", rate: false },
  grandSlams: { label: "Grand Slams", abbrev: "GS", rate: false },
  runBattedIn: { label: "RBI", abbrev: "RBI", rate: false },
  baseOnBalls: { label: "Walks", abbrev: "BB", rate: false },
  intentionBallOnBalls: { label: "Intentional Walks", abbrev: "IBB", rate: false },
  strikeOut: { label: "Strikeouts", abbrev: "SO", rate: false },
  sacrificeHit: { label: "Sacrifice Hits", abbrev: "SH", rate: false },
  sacrificeFly: { label: "Sacrifice Flies", abbrev: "SF", rate: false },
  hitByPitch: { label: "Hit By Pitch", abbrev: "HBP", rate: false },
  groundIntoDoublePlay: { label: "GIDP", abbrev: "GIDP", rate: false },
  battingAverage: { label: "Batting Average", abbrev: "AVG", rate: true },
  onBasePercentage: { label: "On-Base %", abbrev: "OBP", rate: true },
  sluggingAverage: { label: "Slugging %", abbrev: "SLG", rate: true },
};

const getCatalogFieldMeta = (catalog, stat) => catalog?.fieldMeta?.[stat];

export const getStatLabel = (stat, catalog = null) =>
  getCatalogFieldMeta(catalog, stat)?.label ||
  QUERY_BUILDER_STAT_META[stat]?.label ||
  stat.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

export const getStatAbbrev = (stat, catalog = null) =>
  getCatalogFieldMeta(catalog, stat)?.abbrev ||
  QUERY_BUILDER_STAT_META[stat]?.abbrev ||
  getStatLabel(stat, catalog);

export const isRateStat = (stat, catalog = null) =>
  Boolean(getCatalogFieldMeta(catalog, stat)?.rate || QUERY_BUILDER_STAT_META[stat]?.rate);

export const parseQueryStatNumber = (value) => {
  if (value == null || value === "") return null;
  const num =
    typeof value === "number" ? value : parseFloat(String(value).replace(/,/g, ""));
  return Number.isNaN(num) ? null : num;
};

export const formatQueryStatValue = (stat, value, catalog = null) => {
  if (value == null || value === "") return "—";
  const num = parseQueryStatNumber(value);
  const format = getCatalogFieldMeta(catalog, stat)?.format;
  if (format === "decimal1") {
    return num == null ? value : num.toFixed(1);
  }
  if (format === "pct" || format === "rate" || isRateStat(stat, catalog)) {
    if (num == null) return value;
    return num.toFixed(3).replace(/^0(?=\.)/, "");
  }
  return num == null ? value : num;
};

export const cloneQueryParams = (params) => ({
  ...params,
  selectedStats: [...(params.selectedStats || [])],
  statisticalFilters: Object.fromEntries(
    Object.entries(params.statisticalFilters || {}).map(([k, v]) => [
      k,
      v && typeof v === "object" ? { ...v } : v,
    ])
  ),
  biographicalFilters: { ...(params.biographicalFilters || {}) },
  statusFilters: { ...(params.statusFilters || {}) },
  teams: [...(params.teams || [])],
});

const shouldSetCatalogGroupId = (catalog) =>
  Boolean(catalog && catalog.sport !== "mlb" && catalog.defaultGroupId);

const resolveCatalogGroupId = (catalog, groupId) => {
  if (!catalog) return undefined;
  if (groupId && catalog.landingByGroup?.[groupId]) return groupId;
  return catalog.defaultGroupId;
};

const getLandingParams = (catalog = null, groupId) => {
  if (!catalog) return DEFAULT_QUERY_PARAMS;
  const resolvedGroupId = resolveCatalogGroupId(catalog, groupId);
  return (
    catalog.landingByGroup?.[resolvedGroupId] ||
    catalog.landingQuery ||
    catalog.landingByGroup?.[catalog.defaultGroupId] ||
    DEFAULT_QUERY_PARAMS
  );
};

const getLandingDisplayStats = (catalog, groupId) => {
  const landingStats = getLandingParams(catalog, groupId).selectedStats;
  if (landingStats?.length > 0) return landingStats;
  const resolvedGroupId = resolveCatalogGroupId(catalog, groupId);
  return catalog?.fieldsByGroup?.[resolvedGroupId] || LANDING_DISPLAY_STATS;
};

const getCatalogRecommendedFilters = (params, catalog = null) => {
  if (!catalog) return RECOMMENDED_FILTERS;
  const groupId = resolveCatalogGroupId(catalog, params.groupId);
  return (
    catalog.recommendedByGroup?.[groupId] ||
    catalog.recommendedFilters ||
    RECOMMENDED_FILTERS
  );
};

export const getRecommendedThreshold = (filter, queryType) =>
  queryType === "season" ? filter.seasonValue : filter.careerValue;

export const isRecommendedFilterActive = (params, filter) => {
  const applied = params.statisticalFilters?.[filter.stat];
  return applied?.operator === ">=" && Number(applied.value) > 0;
};

export const toggleRecommendedFilter = (params, filterId, catalog = null) => {
  const filter = getCatalogRecommendedFilters(params, catalog).find(
    (item) => item.id === filterId
  );
  if (!filter) return cloneQueryParams(params);
  const next = cloneQueryParams(params);
  const landing = getLandingParams(catalog, next.groupId);
  const landingStats = getLandingDisplayStats(catalog, next.groupId);
  if (isRecommendedFilterActive(next, filter)) {
    delete next.statisticalFilters[filter.stat];
    if (!landingStats.includes(filter.stat)) {
      next.selectedStats = next.selectedStats.filter((stat) => stat !== filter.stat);
    }
    if (next.sortBy === filter.stat) {
      next.sortBy = landing.sortBy;
      next.sortDirection = landing.sortDirection;
    }
    return next;
  }
  if (!next.selectedStats.includes(filter.stat)) {
    next.selectedStats = [...next.selectedStats, filter.stat];
  }
  next.statisticalFilters[filter.stat] = {
    operator: filter.operator,
    value: getRecommendedThreshold(filter, next.queryType),
  };
  next.sortBy = filter.stat;
  next.sortDirection = -1;
  return next;
};

export const toggleStatSelection = (params, stat) => {
  const next = cloneQueryParams(params);
  const isSelected = next.selectedStats.includes(stat);

  next.selectedStats = isSelected
    ? next.selectedStats.filter((item) => item !== stat)
    : [...next.selectedStats, stat];

  if (isSelected) {
    delete next.statisticalFilters[stat];
  } else {
    next.statisticalFilters[stat] = { operator: ">=", value: 0 };
  }

  if (isSelected && next.sortBy === stat) {
    next.sortBy = "";
    next.sortDirection = -1;
  }

  return next;
};

export const updateStatFilter = (params, stat, field, value) => {
  const next = cloneQueryParams(params);
  const processedValue = field === "value" ? parseFloat(value) || 0 : value;

  next.statisticalFilters = {
    ...next.statisticalFilters,
    [stat]: {
      operator: ">=",
      ...next.statisticalFilters[stat],
      [field]: processedValue,
    },
  };
  next.sortBy = stat;
  next.sortDirection = -1;

  return next;
};

export const isLandingSort = (params, catalog = null) => {
  const landing = getLandingParams(catalog, params.groupId);
  return (
    params.sortBy === landing.sortBy &&
    Number(params.sortDirection) === Number(landing.sortDirection)
  );
};

export const cleanStatisticalFilters = (filters = {}) => {
  const cleaned = {};
  Object.entries(filters).forEach(([stat, filter]) => {
    if (filter && typeof filter === "object" && filter.operator && filter.value !== undefined) {
      cleaned[stat] = {
        operator: filter.operator,
        value:
          typeof filter.value === "string"
            ? parseFloat(filter.value) || 0
            : filter.value,
      };
    }
  });
  return cleaned;
};

export const parseQueryParamsFromSearch = (searchParams, catalog = null) => {
  const urlParams = {};
  searchParams.forEach((value, key) => {
    try {
      urlParams[key] = JSON.parse(value);
    } catch {
      urlParams[key] = value;
    }
  });

  const groupId = resolveCatalogGroupId(catalog, urlParams.groupId);
  const landing = getLandingParams(catalog, groupId);

  if (Object.keys(urlParams).length === 0) {
    const params = cloneQueryParams(landing);
    if (shouldSetCatalogGroupId(catalog)) {
      params.groupId = groupId;
    }
    return params;
  }

  const sortBy =
    urlParams.sortBy || urlParams.sortField || landing.sortBy;
  const sortDirectionRaw =
    urlParams.sortDirection ??
    urlParams.sortOrder ??
    landing.sortDirection;
  const sortDirection =
    sortDirectionRaw === "desc" ||
    sortDirectionRaw === -1 ||
    sortDirectionRaw === "-1"
      ? -1
      : sortDirectionRaw === "asc" ||
          sortDirectionRaw === 1 ||
          sortDirectionRaw === "1"
        ? 1
        : landing.sortDirection;

  const parsed = {
    ...cloneQueryParams(landing),
    ...urlParams,
    sortBy: sortBy === "bio.birthName" ? QUERY_NAME_SORT_FIELD : sortBy,
    sortDirection,
    selectedStats: urlParams.selectedStats || [...(landing.selectedStats || [])],
    teams: urlParams.teams || [...(landing.teams || [])],
    statisticalFilters: urlParams.statisticalFilters
      ? cleanStatisticalFilters(urlParams.statisticalFilters)
      : {},
    biographicalFilters: urlParams.biographicalFilters || {
      ...(landing.biographicalFilters || {}),
    },
    statusFilters: urlParams.statusFilters || { ...(landing.statusFilters || {}) },
  };
  if (shouldSetCatalogGroupId(catalog)) {
    parsed.groupId = groupId;
  }
  return parsed;
};

export const dismissFilterChip = (params, chipKey, catalog = null) => {
  const next = cloneQueryParams(params);
  const landing = getLandingParams(catalog, next.groupId);
  const landingStats = getLandingDisplayStats(catalog, next.groupId);
  if (chipKey === "queryType") {
    next.queryType = landing.queryType;
    return next;
  }
  if (chipKey === "group") {
    return cloneQueryParams(getLandingParams(catalog, catalog?.defaultGroupId));
  }
  if (chipKey === "years") {
    next.startYear = landing.startYear;
    next.endYear = landing.endYear;
    return next;
  }
  if (chipKey === "sort") {
    next.sortBy = landing.sortBy;
    next.sortDirection = landing.sortDirection;
    return next;
  }
  if (chipKey.startsWith("stat-")) {
    const stat = chipKey.slice("stat-".length);
    delete next.statisticalFilters[stat];
    if (!landingStats.includes(stat)) {
      next.selectedStats = next.selectedStats.filter((item) => item !== stat);
    }
    if (next.sortBy === stat) {
      next.sortBy = landing.sortBy;
      next.sortDirection = landing.sortDirection;
    }
    return next;
  }
  if (chipKey.startsWith("team-")) {
    const team = chipKey.slice("team-".length);
    next.teams = next.teams.filter((item) => item !== team);
    return next;
  }
  if (chipKey.startsWith("bio-")) {
    const field = chipKey.slice("bio-".length);
    next.biographicalFilters = { ...next.biographicalFilters, [field]: "" };
    return next;
  }
  return next;
};

export const formatShowPlayersLabel = ({ total, isError, hasLoaded }) => {
  if (isError || !hasLoaded) return "Show players";
  if (total === 0) return "No players";
  if (total > 1000) return "Show 1,000+ players";
  return `Show ${Number(total).toLocaleString("en-US")} players`;
};

export const isShowPlayersDisabled = ({ total, isError, hasLoaded }) =>
  Boolean(hasLoaded && !isError && total === 0);

export const getPlayerDisplayName = (result) =>
  scoreboardNameFromPlayer(result, result?.playerId || "Unknown player");

export const getResultStatValue = (result, stat, queryType) => {
  if (queryType === "season") {
    return result[stat] ?? result.seasonStats?.[stat];
  }
  return result[`career_${stat}`] ?? result.careerStats?.[stat];
};

export const buildActiveFilterChips = (queryParams, catalog = null) => {
  const chips = [];
  const landing = getLandingParams(catalog, queryParams.groupId);
  const queryTypes = catalog?.queryTypes || QUERY_TYPES;

  if (queryParams.queryType !== landing.queryType) {
    const type = queryTypes.find((t) => t.value === queryParams.queryType);
    chips.push({ key: "queryType", label: type?.label || queryParams.queryType });
  }

  if (
    shouldSetCatalogGroupId(catalog) &&
    queryParams.groupId &&
    queryParams.groupId !== catalog.defaultGroupId
  ) {
    const group = catalog.groups?.find((item) => item.id === queryParams.groupId);
    chips.push({ key: "group", label: group?.label || queryParams.groupId });
  }

  if (
    queryParams.startYear !== landing.startYear ||
    queryParams.endYear !== landing.endYear
  ) {
    chips.push({
      key: "years",
      label: `${queryParams.startYear}–${queryParams.endYear}`,
    });
  }

  Object.entries(queryParams.statisticalFilters || {}).forEach(([stat, filter]) => {
    if (filter?.value == null || filter.value === "") return;
    const landingFilter = landing.statisticalFilters?.[stat];
    if (
      landingFilter &&
      filter.operator === landingFilter.operator &&
      Number(filter.value) === Number(landingFilter.value)
    ) {
      return;
    }
    const op = filter.operator === ">=" ? "≥" : filter.operator === "<=" ? "≤" : "=";
    chips.push({
      key: `stat-${stat}`,
      label: `${getStatAbbrev(stat, catalog)} ${op} ${formatQueryStatValue(
        stat,
        filter.value,
        catalog
      )}`,
    });
  });

  (queryParams.teams || []).forEach((team) => {
    chips.push({ key: `team-${team}`, label: team });
  });

  Object.entries(queryParams.biographicalFilters || {}).forEach(([field, value]) => {
    if (value) {
      const fieldMeta = BIO_FILTER_FIELDS.find((f) => f.value === field);
      chips.push({ key: `bio-${field}`, label: `${fieldMeta?.label || field}: ${value}` });
    }
  });

  if (!isLandingSort(queryParams, catalog) && queryParams.sortBy) {
    const sortLabel = isQueryNameSort(queryParams.sortBy)
      ? "Player name"
      : getStatLabel(queryParams.sortBy, catalog);
    chips.push({
      key: "sort",
      label: `Sort: ${sortLabel} ${queryParams.sortDirection === 1 ? "↑" : "↓"}`,
    });
  }

  return chips;
};

export const isQueryNameSort = (sortBy) =>
  sortBy === QUERY_NAME_SORT_FIELD || sortBy === "bio.birthName";

export const getQueryDisplayStats = (queryParams, catalog = null) =>
  queryParams.selectedStats?.length > 0
    ? queryParams.selectedStats
    : [...getLandingDisplayStats(catalog, queryParams.groupId)];

export const buildQueryResultRows = (queryParams, results = [], catalog = null) => {
  if (queryParams.queryType === "team") {
    return results.map((result, index) => ({
      id: result._id || `team-${index}`,
      rank: index + 1,
      team: result._id || "Unknown team",
      playerCount: Array.isArray(result.players)
        ? result.players.length
        : result.totalPlayers || 0,
      seasonCount: Array.isArray(result.seasons) ? result.seasons.length : 0,
    }));
  }

  const stats = getQueryDisplayStats(queryParams, catalog);
  return results.map((result, index) => {
    const playerKey = result.playerId || result._id || index;
    const row = {
      id:
        queryParams.queryType === "season"
          ? `${playerKey}-${result.year ?? index}`
          : playerKey,
      rank: index + 1,
      playerId: result.playerId,
      playerName: getPlayerDisplayName(result),
      year: result.year,
      team: result.team,
    };
    stats.forEach((stat) => {
      row[stat] = parseQueryStatNumber(
        getResultStatValue(result, stat, queryParams.queryType)
      );
    });
    return row;
  });
};

export const getGridSortModel = (queryParams) => {
  if (!queryParams?.sortBy) return [];
  const field = isQueryNameSort(queryParams.sortBy)
    ? GRID_PLAYER_NAME_FIELD
    : queryParams.sortBy;
  return [
    {
      field,
      sort: Number(queryParams.sortDirection) === 1 ? "asc" : "desc",
    },
  ];
};

export const applyGridSortModel = (params, sortModel = [], catalog = null) => {
  const next = cloneQueryParams(params);
  const entry = sortModel[0];
  if (!entry?.field || !entry.sort) {
    const landing = getLandingParams(catalog, next.groupId);
    next.sortBy = landing.sortBy;
    next.sortDirection = landing.sortDirection;
    return next;
  }
  next.sortBy =
    entry.field === GRID_PLAYER_NAME_FIELD
      ? QUERY_NAME_SORT_FIELD
      : entry.field;
  next.sortDirection = entry.sort === "asc" ? 1 : -1;
  return next;
};

export const formatQueryResultsCaption = (queryParams, totalResults) => {
  const noun = queryParams.queryType === "team" ? "teams" : "players";
  const count = `${Number(totalResults || 0).toLocaleString("en-US")} ${noun} matched`;
  if (queryParams.queryType === "team" || !queryParams.sortBy) return count;
  const sortLabel = isQueryNameSort(queryParams.sortBy)
    ? "player name"
    : getStatLabel(queryParams.sortBy);
  const arrow = Number(queryParams.sortDirection) === 1 ? "↑" : "↓";
  return `${count} · sorted by ${sortLabel} ${arrow}`;
};
